import React, { useState, useEffect, useRef, memo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AppSelect } from '../../../../components/common/select';
import api from '../../../../api';

const ServiceSection = ({
  index,
  onDelete,
  control,
  watch,
  setValue,
  handleSelectChange,
  handleTextInputChange,
  handleTextInputBlur,
  servicesOptions,
  standardsOptions,
  formErrors,
  errors,
  isIsccService
}) => {
  const [isccScopeOptions, setIsccScopeOptions] = useState([]);
  const [isIscc, setIsIscc] = useState(false);

  // ✅ PERFORMANCE: Only watch specific fields needed for this section
  const selectedServiceId = useWatch({ control, name: `services-${index}` });
  const standardRows = useWatch({ control, name: `standards-${index}` }) || [];

  // Check if selected service is ISCC
  useEffect(() => {
    if (selectedServiceId && servicesOptions) {
      let selectedService = servicesOptions.find(opt => opt.id === selectedServiceId);
      if (!selectedService) {
        selectedService = servicesOptions.find(opt => opt.value === selectedServiceId);
      }
      if (!selectedService) {
        selectedService = servicesOptions.find(opt => String(opt.id) === selectedServiceId);
      }
      if (!selectedService) {
        selectedService = servicesOptions.find(opt => String(opt.value) === selectedServiceId);
      }

      const serviceName = selectedService?.name || selectedService?.label || '';
      const isIsccValue = serviceName.toLowerCase().includes('iscc') || serviceName.toLowerCase().includes('sustainability');
      setIsIscc(isIsccValue);

      if (isIsccValue) {
        fetchIsccScopeOptions();
      }
    } else {
      setIsIscc(false);
    }
  }, [selectedServiceId, index, servicesOptions]);

  const fetchIsccScopeOptions = async () => {
    try {
      let response;
      try {
        response = await api.get('/md/iscc-scope/all');
      } catch (allEndpointError) {
        try {
          response = await api.get('/md/iscc-scope?page=0&size=1000&search=');
        } catch (endpointError) {
          const alternativeEndpoints = [
            '/api/md/iscc-scope/all',
            '/crm/iscc-scope/all',
            '/master/iscc-scope/all',
            '/md/iscc-scope/all',
            '/api/md/iscc-scope?page=0&size=1000&search=',
            '/crm/iscc-scope?page=0&size=1000&search=',
            '/master/iscc-scope?page=0&size=1000&search=',
            '/md/scope'
          ];

          for (const endpoint of alternativeEndpoints) {
            try {
              response = await api.get(endpoint);
              break;
            } catch (altError) {
              // Continue to next endpoint
            }
          }

          if (!response) {
            throw new Error('All endpoints failed');
          }
        }
      }

      let scopeData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          scopeData = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          scopeData = response.data.content;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          scopeData = response.data.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          scopeData = response.data.items;
        } else if (response.data.records && Array.isArray(response.data.records)) {
          scopeData = response.data.records;
        } else {
          scopeData = [];
        }
      }

      if (scopeData.length > 0) {
        const formattedOptions = scopeData.map(scope => ({
          value: scope.id || scope.value || scope.code,
          label: scope.code && scope.name ? `${scope.code} - ${scope.name}` : (scope.label || scope.name || 'Unknown Scope')
        }));
        setIsccScopeOptions(formattedOptions);
      } else {
        setIsccScopeOptions([
          { value: 'fallback-1', label: 'ISCC Scope 1 - Fallback Option' },
          { value: 'fallback-2', label: 'ISCC Scope 2 - Fallback Option' },
          { value: 'fallback-3', label: 'ISCC Scope 3 - Fallback Option' }
        ]);
      }
    } catch (error) {
      setIsccScopeOptions([
        { value: 'fallback-1', label: 'ISCC Scope 1 - Fallback Option' },
        { value: 'fallback-2', label: 'ISCC Scope 2 - Fallback Option' },
        { value: 'fallback-3', label: 'ISCC Scope 3 - Fallback Option' }
      ]);
    }
  };

  // standards-${index} and scope-${index} are arrays, one entry per standard
  // row (added via the "Add Standard" button) so each standard keeps its own scope.
  const rows = standardRows.length > 0 ? standardRows : [''];
  const scopeRows = watch(`scope-${index}`) || [];

  // Local draft for plain-text scope fields — avoids parent re-render on every keystroke.
  // Synced to parent/RHF on blur only.
  const localScopeRef = useRef({});
  const [localScopeDraft, setLocalScopeDraft] = useState({});

  // Keep local draft in sync when scopeRows changes externally (e.g. form reset / edit init).
  // Keyed on the VALUES, not just the row count: opening a lead for edit pushes the saved scopes
  // in with setValue after mount, often onto rows that already exist ([''] -> ['Manufacturing']).
  // A length-only dependency missed that, and the stale '' draft then won the ?? below the
  // real value — the field rendered blank on every edit.
  const scopeRowsKey = JSON.stringify(scopeRows);
  useEffect(() => {
    const next = {};
    scopeRows.forEach((v, i) => { if (!isIscc) next[i] = v || ''; });
    setLocalScopeDraft(next);
    localScopeRef.current = next;
  // eslint-disable-next-line
  }, [scopeRowsKey, isIscc]);

  const updateRow = (rowIdx, standardId) => {
    const updatedStandards = [...rows];
    updatedStandards[rowIdx] = standardId;
    handleSelectChange(`standards-${index}`, updatedStandards);
  };

  const updateRowScope = (rowIdx, scopeValue) => {
    const updatedScope = [...scopeRows];
    updatedScope[rowIdx] = scopeValue;
    handleSelectChange(`scope-${index}`, updatedScope);
  };

  // Used by plain-text textarea: update local draft only (no parent re-render)
  const handleScopeTextChange = (rowIdx, value) => {
    localScopeRef.current = { ...localScopeRef.current, [rowIdx]: value };
    setLocalScopeDraft(prev => ({ ...prev, [rowIdx]: value }));
  };

  // Sync local draft to parent/RHF on blur
  const handleScopeTextBlur = (rowIdx) => {
    const value = localScopeRef.current[rowIdx] ?? '';
    if (value !== (scopeRows[rowIdx] ?? '')) {
      updateRowScope(rowIdx, value);
    }
  };

  const handleAddStandard = () => {
    handleSelectChange(`standards-${index}`, [...rows, '']);
    handleSelectChange(`scope-${index}`, [...scopeRows, isIscc ? [] : '']);
  };

  const handleRemoveStandard = (rowIdx) => {
    if (rows.length <= 1) return;
    handleSelectChange(`standards-${index}`, rows.filter((_, i) => i !== rowIdx));
    handleSelectChange(`scope-${index}`, scopeRows.filter((_, i) => i !== rowIdx));
  };

  const selectedElsewhere = (rowIdx) => rows.filter((id, i) => i !== rowIdx && id);

  return (
    <div className="card mb-3">
      <div className="card-header d-flex align-items-center justify-content-between py-2">
        <span className="fw-medium">Service {index + 1}</span>
        {index > 0 && (
          <button
            type="button"
            className="btn btn-icon btn-ghost-danger btn-sm"
            onClick={() => onDelete(index)}
            title="Remove service"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M4 7l16 0"></path>
              <path d="M10 11l0 6"></path>
              <path d="M14 11l0 6"></path>
              <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
              <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
            </svg>
          </button>
        )}
      </div>
      <div className="card-body">
        <div className="row mb-3">
          <div className="col-6">
            <Controller
              name={`services-${index}`}
              control={control}
              render={({ field }) => (
                <AppSelect
                  name={`services-${index}`}
                  label="Services"
                  value={field.value || ''}
                  onChange={handleSelectChange}
                  options={servicesOptions || []}
                  placeholder="Select services"
                  required={true}
                  error={formErrors?.[`services-${index}`] || errors[`services-${index}`]?.message}
                  disabled={!servicesOptions || servicesOptions.length === 0}
                  valueField="id"
                  labelField="name"
                  searchField="name"
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-2">
            <label className="form-label mb-0">Standards</label>
          </div>
          {rows.map((standardId, rowIdx) => (
            <div className="col-12 mb-3" key={rowIdx}>
              <div className="p-3 border rounded bg-light">
                <div className="row g-2 align-items-start">
                  <div className="col">
                    <AppSelect
                      name={`standards-${index}-${rowIdx}`}
                      value={standardId || ''}
                      onChange={(name, value) => updateRow(rowIdx, value)}
                      options={(standardsOptions || []).filter(opt => !selectedElsewhere(rowIdx).includes(opt.id))}
                      placeholder="Select standard"
                      required={rowIdx === 0}
                      disabled={!standardsOptions || standardsOptions.length === 0}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                  {rows.length > 1 && (
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-icon btn-ghost-danger"
                        onClick={() => handleRemoveStandard(rowIdx)}
                        title="Remove standard"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M4 7l16 0"></path>
                          <path d="M10 11l0 6"></path>
                          <path d="M14 11l0 6"></path>
                          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                          <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                <div className="row g-2 mt-2">
                  <div className="col-12">
                    {isIscc ? (
                      <AppSelect
                        key={`iscc-scope-${index}-${rowIdx}-${isccScopeOptions.length}`}
                        name={`scope-${index}-${rowIdx}`}
                        value={scopeRows[rowIdx] || []}
                        onChange={(name, value) => updateRowScope(rowIdx, value)}
                        options={isccScopeOptions}
                        placeholder="Select scope items"
                        multiple={true}
                        valueField="value"
                        labelField="label"
                        searchField="label"
                      />
                    ) : (
                      <textarea
                        className="form-control"
                        value={localScopeDraft[rowIdx] ?? scopeRows[rowIdx] ?? ''}
                        onChange={(e) => handleScopeTextChange(rowIdx, e.target.value)}
                        onBlur={() => handleScopeTextBlur(rowIdx)}
                        placeholder="Enter scope for this standard (optional, minimum 10 characters if provided)"
                        rows="2"
                        autoComplete="off"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {(formErrors?.[`standards-${index}`] || errors[`standards-${index}`]) && (
            <div className="col-12">
              <div className="text-danger small mt-1">
                {formErrors?.[`standards-${index}`] || errors[`standards-${index}`]?.message}
              </div>
            </div>
          )}
          {(formErrors?.[`scope-${index}`] || errors[`scope-${index}`]) && (
            <div className="col-12">
              <div className="text-danger small mt-1">
                {formErrors?.[`scope-${index}`] || errors[`scope-${index}`]?.message}
              </div>
            </div>
          )}
          <div className="col-12 d-flex justify-content-end mt-1">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAddStandard}
              disabled={!standardsOptions || standardsOptions.length === 0}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path>
                <path d="M5 12l14 0"></path>
              </svg>
              Add Standard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ServiceSection);
